import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import {
  computeManagerKpis,
  filterLotsBySegment,
  sortLotsForQueue,
} from '../../../lib/productionTrackingQueues';
import { applyProductionFilters } from '../../../lib/productionTrackingUtils';
import ProductionLotRow from './ProductionLotRow';

const MANAGER_SEGMENTS = [
  { id: 'active', labelKey: 'productionTracking.segments.active', icon: 'Play' },
  { id: 'pending', labelKey: 'productionTracking.segments.pending', icon: 'Clock' },
  { id: 'urgent_delayed', labelKey: 'productionTracking.segments.urgent', icon: 'AlertTriangle' },
  { id: 'done', labelKey: 'productionTracking.segments.done', icon: 'CheckCircle2' },
];

function KpiTile({ label, value, icon, accent }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3 min-w-[140px] flex-1">
      <div
        className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${accent}`}
      >
        <Icon name={icon} size={20} />
      </div>
      <div>
        <p className="text-2xl font-bold text-foreground tabular-nums">{value}</p>
        <p className="text-xs text-muted-foreground leading-tight">{label}</p>
      </div>
    </div>
  );
}

const ManagerProductionOverview = ({
  batches,
  filters,
  searchQuery,
  userRole,
  canOpenWorkshop,
  visibilityCtx,
  loading,
  onOpenLotDetails,
}) => {
  const { t } = useTranslation();
  const [activeSegment, setActiveSegment] = useState('active');

  const baseList = useMemo(
    () => applyProductionFilters(batches, filters, searchQuery),
    [batches, filters, searchQuery]
  );

  const kpis = useMemo(() => computeManagerKpis(batches), [batches]);

  const counts = useMemo(() => {
    const c = {};
    for (const seg of MANAGER_SEGMENTS) {
      c[seg.id] = filterLotsBySegment(batches, seg.id, visibilityCtx).length;
    }
    return c;
  }, [batches, visibilityCtx]);

  const segmentLots = useMemo(() => {
    const list = filterLotsBySegment(baseList, activeSegment, visibilityCtx);
    return sortLotsForQueue(list);
  }, [baseList, activeSegment, visibilityCtx]);

  if (loading) {
    return (
      <div className="text-center py-14 text-muted-foreground rounded-xl border border-dashed border-border">
        {t('productionTracking.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile
          label={t('productionTracking.kpi.active')}
          value={kpis.active}
          icon="Factory"
          accent="bg-primary/10 text-primary"
        />
        <KpiTile
          label={t('productionTracking.kpi.pending')}
          value={kpis.pending}
          icon="Clock"
          accent="bg-muted text-muted-foreground"
        />
        <KpiTile
          label={t('productionTracking.kpi.urgent')}
          value={kpis.urgentDelayed}
          icon="AlertTriangle"
          accent="bg-warning/10 text-warning"
        />
        <KpiTile
          label={t('productionTracking.kpi.done')}
          value={kpis.done}
          icon="CheckCircle2"
          accent="bg-success/10 text-success"
        />
      </div>

      <div className="space-y-4 min-w-0">
        <div className="flex gap-2 overflow-x-auto pb-1" role="tablist">
          {MANAGER_SEGMENTS.map((seg) => {
            const active = activeSegment === seg.id;
            return (
              <button
                key={seg.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setActiveSegment(seg.id)}
                className={`shrink-0 flex items-center gap-2 min-h-[40px] px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                }`}
              >
                <Icon name={seg.icon} size={14} />
                {t(seg.labelKey)}
                <span className="tabular-nums opacity-80">{counts[seg.id] ?? 0}</span>
              </button>
            );
          })}
        </div>

        <p className="text-sm text-muted-foreground">
          {segmentLots.length} lot{segmentLots.length !== 1 ? 's' : ''}{' '}
          {t('productionTracking.manager.listHint')}
        </p>

        {segmentLots.length === 0 ? (
          <div className="text-center py-12 rounded-xl border border-dashed border-border">
            <p className="text-muted-foreground">{t('productionTracking.manager.empty')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
            {segmentLots.map((batch) => (
              <ProductionLotRow
                key={batch.id}
                batch={batch}
                userRole={userRole}
                canOpenWorkshop={canOpenWorkshop}
                showManagerActions
                onOpenDetails={onOpenLotDetails}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerProductionOverview;
